/* eslint-disable prettier/prettier */
import React, { useEffect, useImperativeHandle, useMemo, useRef } from "react";
import * as d3 from "d3";
import { ScaleOrdinal } from "d3";
import { combineLatest } from "rxjs";
import { useObservable } from "@iventis/utilities";
import { styled } from "@iventis/styles";
import { Axis } from "d3-axis";
import { Selection } from "d3-selection";
import { distinctUntilKeyChanged } from "rxjs/operators";
import { SectionedGroup } from "./types";
import { useViewportStream } from "./hooks";
import { useGroupAxisRowMinWidth } from "./group-axis-row-min-width-hook";
import { distinctUntilPropsChange } from "./distinct-keys-operator";
import { CATEGORY_ROW_HEIGHT } from "./calendar-context";
import { areGroupsEqual } from "../utilities/groups";

interface GroupAxisRowProps {
    groups: SectionedGroup[];
    marginLeft: number;
    marginTop: number;
    tickHeight: number;
    showGrid: boolean;
    isFirstRow: boolean;
}

export interface GroupAxisRowHandle {
    getRangeForGroup(groupId: string): [number, number] | undefined;
    getGroupAtCoord(value: number): SectionedGroup | null;
}

const getGroupById = (scale, groupId: string) => scale.domain().find((sectionedGroup) => sectionedGroup.group.groupId === groupId);

const resolveRange = (scale: ScaleOrdinal<SectionedGroup, number>, groupId: string): [number, number] => {
    const group = getGroupById(scale, groupId);
    if (!group) {
        return undefined;
    }

    const range = scale.range();
    const rangeIndex = range.indexOf(scale(group));
    return [range[rangeIndex], range[rangeIndex + 1]];
};

// after plain text labels are replaced with foreignObjects, their positions are adjusted to place them in the range
// which corresponds to each SectionGroup
const positionLabels = (axisSelection: Selection<SVGSVGElement, unknown, null, undefined>, scale: ScaleOrdinal<SectionedGroup, number>) => {
    axisSelection
        .selectAll("foreignObject")
        .nodes()
        .forEach((node: SVGForeignObjectElement) => {
            const $node = d3.select(node);
            const nodeGroupID = $node.attr("data-group-id");
            const range = resolveRange(scale, nodeGroupID);
            if (range === undefined) {
                throw new Error(`Could not find group ID "${nodeGroupID}" in the scale's domain.
                This can occur when the .domain() for the scale has not been updated before a calendar item referenced it for its range.
                A good place to look might be the main GroupAxisRow useEffect function that calculates whether the groups have changed.`);
            }
            const [start, end] = range;
            $node.attr("width", end - start);
        });

    return axisSelection;
};

// replace original labels in svg Text nodes with ForeignObject elements
const wrapLabels = (axisSelection: Selection<SVGSVGElement, unknown, null, undefined>, scale: ScaleOrdinal<SectionedGroup, number>) => {
    axisSelection
        .selectAll("text")
        .nodes()
        .forEach((node: SVGTextElement) => {
            const groupId = node.textContent.trim();
            // const { height: textHeight } = node.getBBox();
            // const { height: rowHeight } = (node.parentNode as SVGGraphicsElement).getBBox();
            d3.select(node.parentNode as Element)
                .append("foreignObject")
                .attr("width", 100)
                .attr("height", CATEGORY_ROW_HEIGHT)
                .attr("data-group-id", groupId)
                .attr("x", 0)
                .attr("y", -CATEGORY_ROW_HEIGHT)
                .append("xhtml:div")
                .classed("wrapper", true)
                .append("xhtml:div")
                .classed("container", true)
                .text(getGroupById(scale, groupId)?.group?.groupName);
            d3.select(node).remove();
        });
    return axisSelection;
};

// calculates correct range for the slice of the tree given width available
const getTicksRangeFromSlice = (slice: SectionedGroup[], marginLeft: number, width: number): number[] =>
    slice.reduce(
        (acc, group) => {
            acc.push(group.sectionEnd * (width - marginLeft) + marginLeft);
            return acc;
        },
        [slice[0].sectionStart * (width - marginLeft) + marginLeft]
    );

export const GroupAxisRow = React.forwardRef<GroupAxisRowHandle, GroupAxisRowProps>((props, ref) => {
    const ticksScale = useMemo<ScaleOrdinal<SectionedGroup, number>>(() => d3.scaleOrdinal<SectionedGroup, number, never>(), []);
    const props$ = useObservable(props);
    const axisTicksRef = useRef<SVGGElement>();
    const axisGridRef = useRef<SVGGElement>();
    const minWidth$ = useGroupAxisRowMinWidth();
    const viewport$ = useViewportStream();
    const ticksAxis = useMemo<Axis<SectionedGroup>>(() => d3.axisTop(ticksScale).tickFormat((sectionedGroup) => sectionedGroup?.group?.groupId), [ticksScale]);
    const gridAxis = useMemo<Axis<SectionedGroup>>(() => d3.axisTop(ticksScale).tickFormat(() => ""), [ticksScale]);

    useEffect(() => {
        const subscription = combineLatest<[GroupAxisRowProps, number, { height: number }]>([
            props$.pipe(distinctUntilPropsChange(["marginLeft", "marginTop", "groups", "tickHeight", "showGrid", "isFirstRow"])),
            minWidth$,
            viewport$.pipe(distinctUntilKeyChanged("height")),
        ]).subscribe(([{ marginLeft, marginTop, groups, tickHeight, showGrid }, width, { height }]) => {
            const previousTicks = ticksScale.domain();

            /*
                If groups are not equal in their group IDs nestedly, empty the axis HTML contents
                This forces a fresh redraw
            */
            if (!areGroupsEqual(groups, previousTicks)) {
                d3.select(axisTicksRef.current).html(null);
            }

            ticksScale.domain(groups).range(getTicksRangeFromSlice(groups, marginLeft, width));
            ticksAxis.tickSize(tickHeight);

            d3.select(axisTicksRef.current)
                .call(ticksAxis)
                .attr("transform", `translate(0, ${marginTop})`)
                .call(wrapLabels, ticksScale)
                .call(positionLabels, ticksScale)
                .select("path.domain")
                .remove();

            if (showGrid) {
                gridAxis.tickSize(-height);
                d3.select(axisGridRef.current)
                    .call(gridAxis)
                    .attr("transform", `translate(0, ${marginTop})`)
                    .selectAll("g.tick line")
                    .attr("opacity", 0.15)
                    .attr("stroke-width", (sectionedGroup: SectionedGroup, index) => {
                        if (index % 2 === 0) {
                            const range = resolveRange(ticksScale, sectionedGroup.group.groupId);
                            return range[1] - range[0];
                        }
                        return 0;
                    })
                    .attr("transform", (sectionedGroup: SectionedGroup, index, nodesList: SVGLineElement[]) => {
                        const node = nodesList[index];
                        const offset = parseFloat(node.getAttribute("stroke-width")) / 2;
                        return `translate(${offset}, 0)`;
                    });
                d3.select(axisGridRef.current).select("path.domain").remove();
            }
        });
        return () => subscription.unsubscribe();
    }, [props$, ticksScale, ticksAxis]);

    useImperativeHandle(
        ref,
        () => ({
            getRangeForGroup(groupId: string): [number, number] {
                return resolveRange(ticksScale, groupId);
            },
            getGroupAtCoord(value: number): SectionedGroup | null {
                const rangeIndex = ticksScale.range().findIndex((rangeValue) => rangeValue >= value);
                return rangeIndex > 0 ? ticksScale.domain()[rangeIndex - 1] : null;
            },
        }),
        [ticksScale]
    );

    return (
        <g>
            <GroupAxisTicks ref={axisTicksRef} isFirstRow={props.isFirstRow} />
            <GroupAxisGridLines ref={axisGridRef} />
        </g>
    );
});

const GroupAxisTicks = styled.g<{ isFirstRow: boolean }>`
    .domain,
    .tick line {
        stroke: white;
        stroke-width: 2;
    }

    div.wrapper {
        width: 100%;
        height: 100%;
        background: ${(props) => (props.isFirstRow ? "#dbdadb" : "#e8e4e3")};
        font-weight: 600;
        color: #424242;
        display: flex;
        flex-direction: column;
        justify-content: center;
        margin: 0 1px;
        border-bottom: solid 2px white;
        box-sizing: border-box;
    }

    g.tick:first-of-type {
        div.wrapper {
            border-top-left-radius: ${(props) => (props.isFirstRow ? "8px" : 0)};
            margin-left: 0;
        }
    }

    g.tick:last-of-type {
        div.wrapper {
            border-top-right-radius: ${(props) => (props.isFirstRow ? "8px" : 0)};
            margin-right: 0;
        }
    }

    div.container {
        margin: 0 20px;
        text-align: center;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const GroupAxisGridLines = styled.g`
    line {
        stroke: #ccc;
    }
`;
